import moment from 'moment';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import PropTypes from 'prop-types';
import CustomTypeahead from '../../components/CustomTypeahead';
import { Button, FormField, Section, Select, controlClass, textareaClass } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import creditService from '../../services/credit.service';
import creditPaymentService from '../../services/creditPayment.service';
import { currencies, getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_DONE':
		return { ...state, loading: false };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	case 'UPLOAD_REQUEST':
		return { ...state, uploading: true };
	case 'UPLOAD_SUCCESS':
	case 'UPLOAD_FAIL':
		return { ...state, uploading: false };
	default:
		return state;
	}
};

const emptyForm = {
	amount: 0,
	date: moment.utc().toDate(),
	credit: null,
	creditDue: null,
	folder: '',
	observations: '',
	comprobante: null,
	currency: '',
};

const paymentToForm = (payment) => {
	if (!payment) return { ...emptyForm };
	return {
		...emptyForm,
		amount: payment.amount || 0,
		date: payment.date ? moment(payment.date).toDate() : moment.utc().toDate(),
		credit: payment.credit || null,
		creditDue: payment.creditDue || null,
		folder: payment.folder || '',
		observations: payment.observations || '',
		comprobante: payment.comprobante || null,
		currency: payment.currency || '',
	};
};

const toDateInputValue = (value) => {
	if (!value) return '';
	const d = value instanceof Date ? value : new Date(value);
	if (isNaN(d.getTime())) return '';
	return d.toISOString().slice(0, 10);
};

const CreditPaymentEditor = ({
	mode = 'create',
	creditPaymentId = null,
	initialData = null,
	onCancel,
	onSaved,
	cancelLabel = 'Cancelar',
	saveLabel,
	preloadCreditId = null,
}) => {
	const { t } = useLabels();
	const isEditing = mode === 'edit';
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Id injected via ?creditId= after a "crear nueva pasividad" round trip
	// (see linkToCreate on the credit typeahead below). Only meaningful when
	// the field isn't locked by preloadCreditId.
	const injectedCreditId = !preloadCreditId ? searchParams.get('creditId') : null;
	const consumedCreditId = useRef(null);

	const [{ loading, saving, uploading, error }, dispatch] = useReducer(reducer, {
		loading: true,
		saving: false,
		uploading: false,
		error: '',
	});

	const [form, setForm] = useState(() => paymentToForm(initialData));
	const [credits, setCredits] = useState([]);
	const [generatedDues, setGeneratedDues] = useState([]);
	const [disableCreditDue, setDisableCreditDue] = useState(!initialData?.credit);
	const [unpaidAmount, setUnpaidAmount] = useState(0);

	// Keep the URL mirroring the current selection so a manual pick or clear
	// doesn't leave a stale id from an earlier round trip to be re-injected.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	useEffect(() => {
		let isCurrent = true;

		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				if (isEditing && creditPaymentId && !initialData) {
					const requests = [creditPaymentService.getDetails(creditPaymentId)];
					if (!preloadCreditId) {
						requests.push(creditService.list({ populate: false, paid: false }));
					}
					const results = await Promise.all(requests);
					if (!isCurrent) return;
					const paymentRes = results[0];
					const paymentData = paymentRes.data;
					setForm(paymentToForm(paymentData));
					setDisableCreditDue(false);

					const resolvedCreditId = paymentData.credit?._id || paymentData.credit;
					if (!preloadCreditId) {
						setCredits(results[1]?.data || []);
					}
					if (resolvedCreditId) {
						const duesRes = await creditService.getDuesForcredit(resolvedCreditId);
						if (isCurrent) setGeneratedDues(duesRes.data || []);
					}
					dispatch({ type: 'FETCH_SUCCESS' });
					return;
				}

				if (preloadCreditId) {
					const [creditRes, duesRes] = await Promise.all([
						creditService.getDetails(preloadCreditId),
						creditService.getDuesForcredit(preloadCreditId),
					]);
					if (!isCurrent) return;
					const creditData = creditRes.data;
					setForm((prev) => ({ ...prev, credit: creditData, currency: creditData.currency || '', folder: creditData.owner?.folder || '' }));
					setGeneratedDues(duesRes.data || []);
					setDisableCreditDue(false);
				} else {
					const result = await creditService.list({ populate: false, paid: false });
					if (!isCurrent) return;
					setCredits(result.data || []);
					if (initialData) {
						setForm(paymentToForm(initialData));
						if (initialData.credit) {
							setDisableCreditDue(false);
							const creditId = initialData.credit?._id || initialData.credit;
							const duesRes = await creditService.getDuesForcredit(creditId);
							if (isCurrent) setGeneratedDues(duesRes.data || []);
						}
					}
				}
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				const message = getError(ex);
				if (isCurrent) dispatch({ type: 'FETCH_FAIL', payload: message });
				toast.error(isEditing ? t('msg.paymentLoadFailed') : t('msg.creditDuesLoadFailed'));
			} finally {
				if (isCurrent) dispatch({ type: 'FETCH_DONE' });
			}
		};

		fetchData();
		return () => {
			isCurrent = false;
		};
	}, [isEditing, creditPaymentId, preloadCreditId]);

	const updateField = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const loadGeneratedDues = async (selectedCredit) => {
		try {
			const { data } = await creditService.getDuesForcredit(selectedCredit._id);
			setGeneratedDues(data || []);
		} catch (ex) {
			console.error(ex);
			toast.error(t('msg.creditDuesLoadFailed'));
		}
	};

	// Auto-select a credit created via the "crear nueva pasividad" link,
	// which returns here with ?creditId=<id>. credits is already refetched
	// fresh on mount (see the effect above), so find-in-list is enough.
	useEffect(() => {
		if (!injectedCreditId || consumedCreditId.current === injectedCreditId) return;
		const injectedCredit = credits.find((c) => c._id === injectedCreditId);
		if (!injectedCredit) return;
		setForm((prev) => ({ ...prev, credit: injectedCredit, creditDue: null, currency: injectedCredit.currency || '', folder: injectedCredit.owner?.folder || '' }));
		setDisableCreditDue(false);
		setUnpaidAmount(0);
		loadGeneratedDues(injectedCredit);
		consumedCreditId.current = injectedCreditId;
	}, [credits, injectedCreditId]);

	const handleCreditChange = (selected) => {
		if (!selected || selected.length === 0) {
			setForm((prev) => ({ ...prev, credit: null, creditDue: null, currency: '', folder: '' }));
			setDisableCreditDue(true);
			setUnpaidAmount(0);
			syncInjectedParam('creditId', null, consumedCreditId);
			return;
		}
		const selectedCredit = selected[0];
		setForm((prev) => ({ ...prev, credit: selectedCredit, creditDue: null, currency: selectedCredit.currency || '', folder: selectedCredit.owner?.folder || '' }));
		setDisableCreditDue(false);
		setUnpaidAmount(0);
		syncInjectedParam('creditId', selectedCredit._id, consumedCreditId);
		loadGeneratedDues(selectedCredit);
	};

	const handleCreditDueChange = (selected) => {
		if (!selected || selected.length === 0) {
			setForm((prev) => ({ ...prev, creditDue: null, amount: 0 }));
			setUnpaidAmount(0);
			return;
		}
		const selectedCreditDue = selected[0];
		setForm((prev) => ({ ...prev, creditDue: selectedCreditDue, amount: selectedCreditDue.unpaidAmount || 0 }));
		setUnpaidAmount(selectedCreditDue.unpaidAmount || 0);
	};

	const handleAmountChange = (event) => {
		// Keep the raw string in state so a decimal separator survives while it is being typed.
		const raw = event.target.value;
		const parsed = parseFloat(raw);
		if (raw === '' || !unpaidAmount || (!isNaN(parsed) && parsed <= unpaidAmount)) {
			updateField('amount', raw);
		}
	};

	const uploadComprobante = async () => {
		if (!form.comprobante || (typeof form.comprobante === 'object' && !form.comprobante[0])) {
			toast.warning(t('msg.selectFile'));
			return;
		}

		dispatch({ type: 'UPLOAD_REQUEST' });
		const file = form.comprobante[0];
		const formData = new FormData();
		const r = Math.random().toString(36).substring(7);
		formData.append('file', file, (file.name || '').replaceAll(' ', '') + r);
		if (form.comprobante._id) {
			formData.append('documentId', form.comprobante._id);
		}

		try {
			const response = await creditPaymentService.uploadComprobante(formData);
			updateField('comprobante', response.data);
			dispatch({ type: 'UPLOAD_SUCCESS' });
			toast.success(t('msg.fileUploadedCap'));
		} catch (ex) {
			dispatch({ type: 'UPLOAD_FAIL' });
			toast.error(getError(ex));
		}
	};

	const validate = () => {
		const missingFields = [];
		if (!form.credit) missingFields.push(t('entity.credit'));
		if (!form.creditDue) missingFields.push(t('field.creditDue'));
		if (!form.date) missingFields.push(t('field.date'));
		if (!form.amount || form.amount <= 0) missingFields.push(t('field.amount'));

		if (missingFields.length > 0) {
			return `Complete el/los siguiente/s campo/s requerido/s: ${missingFields.join(', ')}.`;
		}
		return '';
	};

	const handleSave = async (event) => {
		event.preventDefault();
		if (loading || saving) return;

		const validationError = validate();
		if (validationError) {
			dispatch({ type: 'SAVE_FAIL', payload: validationError });
			toast.error(validationError);
			return;
		}

		const payload = {
			amount: Number(form.amount),
			date: moment.utc(form.date).format(),
			credit: form.credit?._id || form.credit,
			folder: form.folder,
			observations: form.observations,
			comprobante: form.comprobante?._id || form.comprobante,
			creditDue: form.creditDue?._id || form.creditDue,
			currency: form.currency,
		};

		dispatch({ type: 'SAVE_REQUEST' });
		try {
			let savedPaymentId = creditPaymentId || initialData?._id;
			if (isEditing) {
				await creditPaymentService.edit(savedPaymentId, payload);
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.creditPaymentUpdated'));
			} else {
				const { data } = await creditPaymentService.create(payload);
				savedPaymentId = data?.creditPayment?._id || data?._id || savedPaymentId;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.creditPaymentCreated'));
			}
			if (onSaved) onSaved(savedPaymentId);
		} catch (ex) {
			dispatch({ type: 'SAVE_FAIL', payload: getError(ex) });
			console.error(getError(ex));
			toast.error(isEditing ? t('msg.paymentUpdateFailed') : t('msg.paymentCreateFailed'));
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? t('action.save') : t('action.add'));
	const availableDues = (generatedDues || []).filter((due) => !due.paid);

	return (
		<Form id='credit-payment-editor-form' onSubmit={handleSave} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos del pago.' : 'Completá los datos para registrar el pago.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					<Button variant='primary' icon={isEditing ? <FaRegSave /> : <MdAdd />} type='submit' disabled={saving || loading}>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title={`${t('entity.credit')} y ${t('field.creditDue').toLowerCase()}`}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='credit-payment-credit' label={t('entity.credit')} required>
						<CustomTypeahead
							id='credit-payment-credit'
							labelKey={(option) => option?.customId || ''}
							onChange={handleCreditChange}
							options={credits}
							selected={form.credit ? [form.credit] : []}
							placeholder={t('form.placeholder.searchCredit')}
							linkToCreate={`/AdminScreen/newCredit?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
							newOption='nueva pasividad'
							disabled={saving || !!preloadCreditId}
							variant='entity'
						/>
					</FormField>
					<FormField id='credit-payment-due' label={t('field.creditDue')} required>
						<CustomTypeahead
							id='credit-payment-due'
							labelKey={(option) => option?.customId || ''}
							onChange={handleCreditDueChange}
							options={availableDues}
							selected={form.creditDue ? [form.creditDue] : []}
							placeholder={t('form.placeholder.searchDue')}
							disabled={saving || disableCreditDue}
							variant='entity'
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Datos del pago'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='credit-payment-amount' label={t('field.amount')} required>
						<Form.Control
							id='credit-payment-amount'
							type='number'
							step='any'
							min={0}
							max={unpaidAmount || undefined}
							className={controlClass}
							value={form.amount}
							onChange={handleAmountChange}
						/>
					</FormField>
					<FormField id='credit-payment-date' label={t('field.date')} required>
						<Form.Control
							id='credit-payment-date'
							type='date'
							className={controlClass}
							value={toDateInputValue(form.date)}
							onChange={(event) => updateField('date', event.target.value ? new Date(event.target.value) : null)}
						/>
					</FormField>
					<FormField id='credit-payment-currency' label={t('field.currency')}>
						<Select
							id='credit-payment-currency'
							options={currencies}
							value={form.currency}
							onChange={(event) => updateField('currency', event.target.value)}
							placeholder='Elegí Moneda'
							disabled={saving}
						/>
					</FormField>
					<FormField id='credit-payment-folder' label={t('field.folder')}>
						<Form.Control
							id='credit-payment-folder'
							className={controlClass}
							value={form.folder}
							onChange={(event) => updateField('folder', event.target.value)}
						/>
					</FormField>
				</div>
			</Section>

			<Section title={t('form.comprobante')}>
				<div className='tw:flex tw:items-end tw:gap-3'>
					<FormField id='credit-payment-comprobante' label={t('form.comprobante')} className='tw:flex-1'>
						<Form.Control
							id='credit-payment-comprobante'
							type='file'
							className={controlClass}
							onChange={(event) => updateField('comprobante', event.target.files)}
						/>
					</FormField>
					<Button onClick={uploadComprobante} disabled={uploading || !form.comprobante}>
						{uploading ? 'Subiendo...' : t('action.upload')}
					</Button>
				</div>
			</Section>

			<Section title={t('field.observations')} description='Notas internas o información adicional del pago.'>
				<FormField id='credit-payment-observations' label={t('field.observations')}>
					<Form.Control
						id='credit-payment-observations'
						as='textarea'
						rows={8}
						className={textareaClass}
						value={form.observations}
						onChange={(event) => updateField('observations', event.target.value)}
					/>
				</FormField>
			</Section>
		</Form>
	);
};

CreditPaymentEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	creditPaymentId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	preloadCreditId: PropTypes.string,
};

export default CreditPaymentEditor;
